import { orderBy } from "lodash";
import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from '../../internal';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const ANNOTATION_ENTITY_TOKEN = new StateToken<AnnotationEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_ENTITY);

export interface AnnotationEntityStateModel extends FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.AnnotationEntityModels.AnnotationEntityType> {
  annotationId: string | null;
}

@State<AnnotationEntityStateModel>({
  name: ANNOTATION_ENTITY_TOKEN,
  defaults: {
    annotationId: null,
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class AnnotationEntityState extends HistoryEntityBaseState<FlatModels.AnnotationEntityModels.AnnotationEntityType, AnnotationEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([AnnotationEntityState])
  static annotationId$(state: AnnotationEntityStateModel): string | null {
    return state.annotationId;
  }

  @Selector([AnnotationEntityState])
  static override typedEntities$(state: AnnotationEntityStateModel): { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([AnnotationEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType }): FlatModels.AnnotationEntityModels.AnnotationEntityType[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([AnnotationEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([AnnotationEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
  
  @Selector([
    AnnotationEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSelectedTypedSlideAnnotations$(typedEntities: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType },
                                               currentSelectedSlidePage: FlatModels.ImageEntityModels.Image | null) {
    if (currentSelectedSlidePage && currentSelectedSlidePage.annotations) {
      return currentSelectedSlidePage.annotations.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType });
    }
    return {};
  }

  @Selector([AnnotationEntityState.currentSelectedTypedSlideAnnotations$])
  static currentSelectedSlideAnnotations$(currentSelectedTypedSlideAnnotations: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType }) {
    return Object.values(currentSelectedTypedSlideAnnotations);
  }

  @Selector([
    AnnotationEntityState.typedEntities$,
    FlatStates.ImageEntityState.currentSelectedSlideImage$
  ])
  static currentSelectedTypedSlideImageAnnotations$(typedEntities: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType },
                                                    currentSelectedSlideImage: FlatModels.ImageEntityModels.Image | null) {
    if (currentSelectedSlideImage && currentSelectedSlideImage.annotations) {
      return currentSelectedSlideImage.annotations.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType });
    }
    return {};
  }

  @Selector([AnnotationEntityState.currentSelectedTypedSlideImageAnnotations$])
  static currentSelectedSlideImageAnnotations$(currentSelectedTypedSlideImageAnnotations: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType }) {
    return Object.values(currentSelectedTypedSlideImageAnnotations);
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideImageAnnotations$])
  static currentSelectedSlideImageInteractiveAreas$(currentSelectedSlideImageAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[]) {
    return currentSelectedSlideImageAnnotations.filter((a) => a.type === FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm
      && (a as FlatModels.AnnotationEntityModels.FreeForm).shape === FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea);
  }

  @Selector([AnnotationEntityState.currentSelectedSlideImageAnnotations$])
  static currentSelectedSlideImageAnnotationIds$(currentSelectedSlideImageAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[]) {
    return currentSelectedSlideImageAnnotations && currentSelectedSlideImageAnnotations.map(a => a.id) || [];
  }

  @Selector([
    AnnotationEntityState.currentSelectedTypedSlideAnnotations$,
    AnnotationEntityState.annotationId$
  ])
  static currentSelectedSlideAnnotation$(currentSelectedTypedSlideAnnotations: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType },
                                         annotationId: string | null) {
    return annotationId && currentSelectedTypedSlideAnnotations[annotationId] || null;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideAnnotation$])
  static currentSelectedSlideMarker$(currentSelectedSlideAnnotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    if (currentSelectedSlideAnnotation && currentSelectedSlideAnnotation.type === FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker) {
      return currentSelectedSlideAnnotation as FlatModels.AnnotationEntityModels.Marker;
    }
    return null;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideAnnotation$])
  static currentSelectedSlideFreeForm$(currentSelectedSlideAnnotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    if (currentSelectedSlideAnnotation && currentSelectedSlideAnnotation.type === FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm) {
      return currentSelectedSlideAnnotation as FlatModels.AnnotationEntityModels.FreeForm;
    }
    return null;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideAnnotation$])
  static currentSelectedSlideKeymap$(currentSelectedSlideAnnotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    if (currentSelectedSlideAnnotation && currentSelectedSlideAnnotation.type === FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap) {
      return currentSelectedSlideAnnotation as FlatModels.AnnotationEntityModels.Keymap;
    }
    return null;
  }

  @Selector([
    AnnotationEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static allCurrentSelectedSlideAnnotations$(typedEntities: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType },
                                             currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    return (currentSelectedSlidePage?.annotations || []).reduce((arr, annotationId) => {
      const entity = typedEntities[annotationId];
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.AnnotationEntityModels.AnnotationEntityType[]);
  }

  @Selector([
    AnnotationEntityState.allCurrentSelectedSlideAnnotations$,
    AnnotationEntityState.annotationLabelInfo$,
  ])
  static annotationOptionArray$(allCurrentSelectedSlideAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[],
                                annotationLabelInfo: any) {
    return (translate: TranslateService) => {
      return allCurrentSelectedSlideAnnotations.reduce((arr, annotation) => {
        arr.push({
          value: annotation.id,
          name: annotationLabelInfo(annotation.id, translate)
        });
        return arr;
      }, [] as {value: string, name: string}[]);
    };
  }
  
  @Selector([
    AnnotationEntityState.allCurrentSelectedSlideAnnotations$,
    AnnotationEntityState.annotationLabelInfo$,
  ])
  static imageAnnotationOptionArray$(allCurrentSelectedSlideAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[],
                                     annotationLabelInfo: any) {
    return (image: FlatModels.ImageEntityModels.Image, translate: TranslateService) => {
      let annotations = allCurrentSelectedSlideAnnotations.reduce((arr, annotation) => {
        if (image && image.annotations && !image.annotations.includes(annotation.id)) {
          arr.push({
            value: annotation.id,
            name: annotationLabelInfo(annotation.id, translate)
          });
        }
        return arr;
      }, [] as {value: string, name: string}[]);
      if (annotations.length > 0) {
        annotations = [{value: 'all', name: translate.instant('global.annotation_all')}, ...annotations];
      }
      return annotations;
    };
  }
  
  @Selector([
    AnnotationEntityState.allCurrentSelectedSlideAnnotations$,
    AnnotationEntityState.annotationLabelInfo$,
  ])
  static poiAnnotationOptionArray$(allCurrentSelectedSlideAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[],
                                   annotationLabelInfo: any) {
    return (poi: FlatModels.PointOfInterestEntityModels.PointOfInterest, translate: TranslateService) => {
      let annotations = allCurrentSelectedSlideAnnotations.reduce((arr, annotation) => {
        if (poi && poi.annotationToPointOfInterest && !poi.annotationToPointOfInterest.map(atpoi => atpoi.annotation).includes(annotation.id)) {
          arr.push({
            value: annotation.id,
            name: annotationLabelInfo(annotation.id, translate)
          });
        }
        return arr;
      }, [] as {value: string, name: string}[]);
      if (annotations.length > 0) {
        annotations = [{value: 'all', name: translate.instant('global.annotation_all')}, ...annotations];
      }
      return annotations;
    };
  }
  
  @Selector([
    AnnotationEntityState.allCurrentSelectedSlideAnnotations$,
    AnnotationEntityState.annotationLabelInfo$,
  ])
  static groupAnnotationOptionArray$(allCurrentSelectedSlideAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[],
                                     annotationLabelInfo: any) {
    return (group: FlatModels.GroupEntityModels.Group, translate: TranslateService) => {
      let annotations = allCurrentSelectedSlideAnnotations.reduce((arr, annotation) => {
        if (group && group.annotations && !group.annotations.includes(annotation.id)) {
          arr.push({
            value: annotation.id,
            name: annotationLabelInfo(annotation.id, translate)
          });
        }
        return arr;
      }, [] as {value: string, name: string}[]);
      if (annotations.length > 0) {
        annotations = [{value: 'all', name: translate.instant('global.annotation_all')}, ...annotations];
      }
      return annotations;
    };
  }

  @Selector([
    AnnotationEntityState.typedEntities$,
    FlatStates.AnnotationLabelEntityState.typedEntities$
  ])
  static annotationLabelInfo$(typedAnnotations: { [id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType },
                              typedAnnotationLabels: { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel }) {
    return (id: string,
            translate: TranslateService) => {
      // Get Annotation
      const annotation = typedAnnotations[id];
      if (annotation) {
        // Get Label
        const annotationLabel = typedAnnotationLabels[annotation.label];

        // Format Text
        let annotationInfo: string;
        if (annotation && annotationLabel) {
          if (Array.isArray(annotationLabel.text)) {
            annotationInfo = annotationLabel.text[0];
          } else {
            annotationInfo = annotationLabel.text;
          }
        } else {
          annotationInfo = annotation.type;
        }

        // Free Form
        if (!(annotationInfo && annotationInfo !== ' ')) {
          const freeform = (annotation as FlatModels.AnnotationEntityModels.FreeForm)
          if (freeform.shape) {
            translate.get(freeform.shape).subscribe((shape: string) => {
              annotationInfo += `Form: ${shape}\t`;
            });
          }
          annotationInfo += `ID: ${annotation.id}`;
        }

        return annotationInfo;
      }
      return '';
    }
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideAnnotation$,
    FlatStates.AnnotationLabelEntityState.typedEntities$
  ])
  static currentSelectedSlideAnnotationLabel$(currentSelectedSlideAnnotation: FlatModels.AnnotationEntityModels.AnnotationEntityType,
                                              typedAnnotationLabels: { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel }) {
    return currentSelectedSlideAnnotation && typedAnnotationLabels[currentSelectedSlideAnnotation.label] || null;
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideAnnotation$,
    FlatStates.AnnotationSourceEntityState.typedEntities$
  ])
  static currentSelectedSlideAnnotationSource$(currentSelectedSlideAnnotation: FlatModels.AnnotationEntityModels.AnnotationEntityType,
                                               typedAnnotationSources: { [id: string]: FlatModels.AnnotationSourceEntityModels.AnnotationSource }) {
    return currentSelectedSlideAnnotation && typedAnnotationSources[currentSelectedSlideAnnotation.source] || null;
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideMarker$,
    FlatStates.AnnotationTargetEntityState.typedEntities$
  ])
  static currentSelectedSlideMarkerTargets$(currentSelectedSlideMarker: FlatModels.AnnotationEntityModels.Marker,
                                            typedAnnotationTargets: { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget }) {
    if (currentSelectedSlideMarker) {
      return currentSelectedSlideMarker.targets.reduce((arr, id) => {
        const entity = typedAnnotationTargets[id];
        
        if (entity) {
          arr.push(entity);
        }
        
        return arr;
      }, [] as FlatModels.AnnotationTargetEntityModels.AnnotationTarget[]);
    }
    return [];
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideMarkerTargets$])
  static currentSelectedSlideMarkerFirstTarget$(currentSelectedSlideMarkerTargets: FlatModels.AnnotationTargetEntityModels.AnnotationTarget[]) {
    return currentSelectedSlideMarkerTargets[0] || null;
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideFreeForm$,
    FlatStates.AnnotationTargetEntityState.typedEntities$
  ])
  static currentSelectedSlideFreeFormTarget$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm,
                                             typedAnnotationTargets: { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget }) {
    return currentSelectedSlideFreeForm && currentSelectedSlideFreeForm.target && typedAnnotationTargets[currentSelectedSlideFreeForm.target] || null;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideFreeForm$])
  static currentSelectedSlideIsFreeForm$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm) {
    if (currentSelectedSlideFreeForm) {
      return true;
    }
    return false;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideFreeForm$])
  static currentSelectedSlideFreeFormIsLine$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm) {
    return currentSelectedSlideFreeForm && currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.line || false;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideFreeForm$])
  static currentSelectedSlideFreeFormIsFree$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm) {
    return currentSelectedSlideFreeForm && currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.free || false;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideFreeForm$])
  static currentSelectedSlideFreeFormIsInteractiveArea$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm) {
    return currentSelectedSlideFreeForm && currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea || false;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideFreeForm$])
  static currentSelectedSlideFreeFormIsShape$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm) {
    return currentSelectedSlideFreeForm && (currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.ellipse
    || currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.arrow
    || currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.rectangle
    || currentSelectedSlideFreeForm.shape === FlatModels.AnnotationEntityModels.Shape.enum.triangle) || false;
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideIsFreeForm$,
    AnnotationEntityState.currentSelectedSlideFreeFormIsInteractiveArea$
  ])
  static currentSelectedSlideFreeFormIsDrawable$(currentSelectedSlideFreeFormIsFreeForm: boolean,
                                                 currentSelectedSlideFreeFormIsInteractiveArea: boolean) {
    return currentSelectedSlideFreeFormIsFreeForm || currentSelectedSlideFreeFormIsInteractiveArea;
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideFreeForm$,
    FlatStates.FreeFormStyleEntityState.typedEntities$
  ])
  static currentSelectedSlideFreeFormStyle$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm,
                                            typedFreeFormStyles: { [id: string]: FlatModels.FreeFormStyleEntityModels.FreeFormStyle }) {
    return currentSelectedSlideFreeForm && typedFreeFormStyles[currentSelectedSlideFreeForm.freeFormStyle] || null;
  }
  
  @Selector([AnnotationEntityState.currentSelectedSlideFreeForm$])
  static currentSelectedSlideFreeFormTypeArray$(currentSelectedSlideFreeForm: FlatModels.AnnotationEntityModels.FreeForm) {
    if (currentSelectedSlideFreeForm) {
      switch (currentSelectedSlideFreeForm.shape) {
        case FlatModels.AnnotationEntityModels.Shape.enum.free:
        case FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea:
          return [
            FlatModels.AnnotationEntityModels.Shape.enum.free,
            FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea
          ].map(value => {
            return { value: value, name: 'enum.annotation.' + value };
          });
        default:
          return [
            FlatModels.AnnotationEntityModels.Shape.enum.rectangle,
            FlatModels.AnnotationEntityModels.Shape.enum.ellipse,
            FlatModels.AnnotationEntityModels.Shape.enum.arrow,
            FlatModels.AnnotationEntityModels.Shape.enum.line,
            FlatModels.AnnotationEntityModels.Shape.enum.triangle,
          ].map(value => {
            return { value: value, name: 'enum.annotation.' + value };
          });
      }
    }
    return [];
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideKeymap$,
    FlatStates.KeymapStyleEntityState.typedEntities$
  ])
  static currentSelectedSlideKeymapStyle$(currentSelectedSlideKeymap: FlatModels.AnnotationEntityModels.Keymap,
                                          typedKeymapStyles: { [id: string]: FlatModels.KeymapStyleEntityModels.KeymapStyle }) {
    return currentSelectedSlideKeymap && typedKeymapStyles[currentSelectedSlideKeymap.keymapStyle] || null;
  }
  
  @Selector([
    AnnotationEntityState.currentSelectedSlideKeymap$,
    FlatStates.KeymapColumnEntityState.typedEntities$
  ])
  static currentSelectedSlideKeymapColumns$(currentSelectedSlideKeymap: FlatModels.AnnotationEntityModels.Keymap,
                                            typedKeymapColumns: { [id: string]: FlatModels.KeymapColumnEntityModels.KeymapColumn }) {
    return currentSelectedSlideKeymap
      && orderBy(
        currentSelectedSlideKeymap.columns.reduce((arr, id) => {
          const entity = typedKeymapColumns[id];
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.KeymapColumnEntityModels.KeymapColumn[]),
        (a) => a.order || 0,
        'asc'
      ) as FlatModels.KeymapColumnEntityModels.KeymapColumn[];
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.AnnotationEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<AnnotationEntityStateModel>,
                            action: FlatActions.AnnotationEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<AnnotationEntityStateModel>,
                              action: FlatActions.AnnotationEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.AnnotationEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<AnnotationEntityStateModel>,
                               action: FlatActions.AnnotationEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<AnnotationEntityStateModel>,
                                 action: FlatActions.AnnotationEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.AnnotationEntityActions.AddEntity)
  @Undoable(
    FlatActions.AnnotationEntityActions.AddEntity,
    ANNOTATION_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<AnnotationEntityStateModel>,
                     action: FlatActions.AnnotationEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.AnnotationEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.AnnotationEntityActions.UpdateEntity,
    ANNOTATION_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<AnnotationEntityStateModel>,
                        action: FlatActions.AnnotationEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
  
  @Action(FlatActions.AnnotationEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.AnnotationEntityActions.RemoveEntity,
    ANNOTATION_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<AnnotationEntityStateModel>,
                        action: FlatActions.AnnotationEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.AnnotationEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<AnnotationEntityStateModel>,
                        action: FlatActions.AnnotationEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<AnnotationEntityStateModel>,
                        action: FlatActions.AnnotationEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<AnnotationEntityStateModel>,
                            action: FlatActions.AnnotationEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  @Action(FlatActions.AnnotationEntityActions.SetAnnotationId)
  setAnnotationId({setState}: StateContext<AnnotationEntityStateModel>,
                  {annotationId}: FlatActions.AnnotationEntityActions.SetAnnotationId) {
    setState(
      patch({
        annotationId
      })
    );
    
  }

  //</editor-fold>
}
